import React, { useEffect, useMemo, useState } from 'react';
import { DeleteOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { SbButton, SbSelect, SbSwitch, SbTable, SbTooltip, SbUpload, TooltipTriggerStyled } from 'uiCore';
import SbModal from 'uiCore/SbModal/SbModal';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import useAuthStore from 'Store/useAuthStore';
import { showToast } from 'Store/actions/profileListActions';
import { showSbConfirm } from 'Utils/sbModal';
import { validateExcelFile } from 'Utils/fileValidation';
import {
  downloadRoleReportingTemplate,
  bulkUploadRoleReportingTo,
  getRoleReportingView,
} from 'Api/apiService';
import {
  BulkToolbarStyled,
  BulkToolbarActionsStyled,
  BulkTableWrapperStyled,
  BulkSubmitRowStyled,
  BulkUnresolvedReportingToStyled,
} from './RoleReporting.styles';

const TARGET_SHEET = 'Role Reporting To';
const BATCH_LIMIT = 200;

// Millisecond-resolution Date.now() keys can collide when rows are added/
// parsed within the same millisecond — since updateRow/removeRow match by
// key, a collision would silently mutate/delete the wrong row. Same guarded
// pattern as orgDesignAdapters.js's generateNewRoleId.
const generateRowId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Prefers the RFC 5987 encoded form (filename*=UTF-8''...), which is how a
// non-ASCII filename is actually transmitted — a plain `.split('filename=')`
// would garble it, since that split matches the "filename*=" prefix too and
// leaves the percent-encoded bytes undecoded. Falls back to the plain
// filename= form (today's backend always sends this, hardcoded and ASCII).
const parseContentDispositionFilename = (disposition) => {
  if (!disposition) return null;
  const encodedMatch = disposition.match(/filename\*=(?:UTF-8''|utf-8'')?([^;]+)/i);
  if (encodedMatch) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through to the plain form below
    }
  }
  const plainMatch = disposition.match(/(?<!\*)filename=([^;]+)/i);
  return plainMatch ? plainMatch[1].trim().replace(/^"|"$/g, '') : null;
};

// Matches download_role_reporting_to_template_service's column layout
// (A–S, 0-indexed below). Read POSITIONALLY, not by header name — visible
// headers B–M come from the org's own field_labels config (label()), so
// e.g. "Job Code" can actually read "Job Code & Job Family Name" for a given
// org; only the column position is guaranteed stable.
//
// Column S (role_reporting_to_sb_unique_id) is a live VLOOKUP off column N —
// per the backend's documented contract, it recalculates whenever a human
// picks/changes a manager in Excel and saves, so it's the primary, reliable
// source of the resolved id. It's still a formula cell though, so it reads as
// blank if the file is read without ever being opened+saved in Excel — N's
// raw display text is kept as a fallback for the backend to resolve itself
// in that edge case (see handleSubmitAll).
const COL = {
  OWN_SB_UNIQUE_ID: 0,           // A
  JOB_CODE: 1,                   // B
  JOB_TITLE: 4,                  // E
  BUSINESS_UNIT: 6,              // G
  FUNCTION: 7,                   // H
  JOB_LEVEL: 10,                 // K
  LOCATION: 12,                  // M
  ROLE_REPORTING_TO_DISPLAY: 13, // N
  IS_APEX_ROLE: 14,              // O
  MANAGER_SB_UNIQUE_ID: 18,      // S
};

const EMPTY_ROW = {
  source: 'manual',
  ownSbUniqueId: null,
  jobTitle: '',
  jobCode: '',
  businessUnit: '',
  functionDept: '',
  level: '',
  location: '',
  // Manual rows (Add Row / in-UI edits) resolve straight to an id via the
  // dropdown, since they're picked from roleOptions directly.
  roleReportingToSbUniqueId: null,
  // Excel-imported rows instead carry the raw "Job Title (Job Code)" display
  // text from column N — resolved server-side on submit (see handleSubmitAll).
  roleReportingToDisplay: '',
  isApexRole: false,
};

// A row counts as "has an update" only when Role Reporting To or Is Apex is
// set — every row in the downloaded template has ownSbUniqueId populated
// (it lists every existing JD), so that alone can't be used to filter out
// rows the user didn't touch.
const hasUpdate = (row) =>
  !!row.ownSbUniqueId && (!!row.roleReportingToSbUniqueId || !!row.roleReportingToDisplay || !!row.isApexRole);

const RoleReportingBulkUpload = ({ onSubmitSuccess, onContinue }) => {
  const dispatch = useDispatch();
  const email = useAuthStore((s) => s.email);
  const [downloading, setDownloading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [rows, setRows] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]); // every eligible role — for Add Row's own-role picker and Role Reporting To's manager picker
  // Set once the POST actually succeeds (202, batch queued) — the backend
  // processes it asynchronously via Celery (task_stream:{task_id} in Redis),
  // so this only confirms the batch was accepted, not that any row has been
  // applied yet. Shows a dialog with the task id and a Continue button.
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    if (!email) return;
    (async () => {
      try {
        const data = await getRoleReportingView(email);
        setRoleOptions(
          (Array.isArray(data) ? data : [])
            .filter((r) => r?.sb_unique_id)
            .map((r) => ({
              value: r.sb_unique_id,
              label: r.job_code ? `${r.job_title} (${r.job_code})` : r.job_title || r.sb_unique_id,
              row: r,
            }))
        );
      } catch {
        setRoleOptions([]);
      }
    })();
  }, [email]);

  const updateRow = (key, changes) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...changes } : r)));

  const addRow = () => {
    setRows((prev) => [...prev, { ...EMPTY_ROW, key: `manual-${generateRowId()}` }]);
  };

  const removeRow = (key) =>
    showSbConfirm({
      title: 'Remove row',
      content: 'This action cannot be undone.',
      okText: 'Remove',
      cancelText: 'Cancel',
      onOk: () => setRows((prev) => prev.filter((r) => r.key !== key)),
    });

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await downloadRoleReportingTemplate(email);
      const url = URL.createObjectURL(response.data);
      const disposition = response.headers['content-disposition'];
      const filename = parseContentDispositionFilename(disposition) || 'role_reporting_template.xlsx';
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      dispatch(showToast('Failed to download template. Please try again.', 'error'));
    } finally {
      setDownloading(false);
    }
  };

  const parseExcelFile = (file) => {
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const sheetName = workbook.SheetNames.includes(TARGET_SHEET) ? TARGET_SHEET : workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // Positional array-of-arrays, header row skipped — every field is
        // read by column index (see COL) since visible headers are
        // org-configurable text, not stable keys.
        const aoaRows = XLSX.utils.sheet_to_json(sheet, { header: 1, range: 1, defval: '' });
        const str = (v) => (v === '' || v == null ? '' : String(v).trim());

        // Label -> id, but ONLY when the label is unique across roleOptions.
        // Two different JDs can render the identical "Job Title (Job Code)"
        // display string (job codes aren't guaranteed unique across legal
        // entities/business units), so guessing via .find() on a colliding
        // label would silently resolve to whichever JD happened to be
        // listed first — not necessarily the one the user meant. Built once
        // up front (not per-row) since it doesn't depend on the row.
        //
        // Normalized (trim + lowercase) rather than exact-string, so an
        // incidental formatting difference between this fallback source
        // (getRoleReportingView) and the backend's own REPORTING_TO_LOOKUP
        // sheet (trailing whitespace, casing) doesn't silently fail to match
        // — a genuine miss still falls through to "unresolved" (flagged in
        // the UI), which is the correct failure mode either way.
        const normalizeLabel = (s) => s.trim().toLowerCase();
        const labelCounts = new Map();
        roleOptions.forEach((o) => {
          const key = normalizeLabel(o.label);
          labelCounts.set(key, (labelCounts.get(key) || 0) + 1);
        });
        const idByUniqueLabel = new Map(
          roleOptions
            .filter((o) => labelCounts.get(normalizeLabel(o.label)) === 1)
            .map((o) => [normalizeLabel(o.label), o.value])
        );

        let conflictCount = 0;

        const edited = aoaRows
          .map((cells, idx) => {
            const ownId = str(cells[COL.OWN_SB_UNIQUE_ID]);
            const reportingToDisplay = str(cells[COL.ROLE_REPORTING_TO_DISPLAY]);
            // The template's dropdown only offers "True"/"False", but accept
            // the common equivalent spellings too (native XLSX boolean cells
            // stringify to "true"/"false" already via str() above; "yes"/"1"
            // cover a user typing over the dropdown instead of selecting it).
            const isApexRole = ['true', 'yes', '1'].includes(str(cells[COL.IS_APEX_ROLE]).toLowerCase());
            // Both Is Apex and Role Reporting To were filled in on the same
            // row — mutually exclusive (an apex role has no manager), and
            // previously resolved by silently discarding the manager value
            // with no indication to the user that anything was dropped.
            if (isApexRole && reportingToDisplay) conflictCount += 1;
            // Column S is the primary source (see COL's comment) — only fall
            // back to resolving N's display text locally when S came back
            // blank (file uploaded without ever being opened+saved in Excel),
            // and only when that text maps to exactly one known role. A row
            // with neither S nor N set is a genuine "no manager yet" row,
            // left as null so the UI renders it as a normal empty, editable
            // dropdown rather than mistaking it for unresolved data.
            const managerId = str(cells[COL.MANAGER_SB_UNIQUE_ID]);
            let resolvedId = !isApexRole && reportingToDisplay
              ? managerId || idByUniqueLabel.get(normalizeLabel(reportingToDisplay)) || null
              : null;
            // A role reporting to itself is rejected by the backend (400),
            // but the manual Add-Row dropdown already excludes this option
            // client-side — the Excel path had no equivalent check, so a
            // self-reference here silently passed through unfiltered. Treat
            // it as unresolved instead: it flags in the UI (per the render
            // below) rather than failing invisibly at submit time.
            if (resolvedId && ownId && resolvedId === ownId) resolvedId = null;
            return {
              ...EMPTY_ROW,
              source: 'excel',
              key: `excel-${idx}-${generateRowId()}`,
              ownSbUniqueId: ownId || null,
              jobCode: str(cells[COL.JOB_CODE]),
              jobTitle: str(cells[COL.JOB_TITLE]),
              businessUnit: str(cells[COL.BUSINESS_UNIT]),
              functionDept: str(cells[COL.FUNCTION]),
              level: str(cells[COL.JOB_LEVEL]),
              location: str(cells[COL.LOCATION]),
              isApexRole,
              roleReportingToSbUniqueId: resolvedId,
              roleReportingToDisplay: !isApexRole ? reportingToDisplay : '',
            };
          })
          // Filter for actual edits BEFORE capping — the sheet lists every
          // active JD (hundreds of rows), so slicing first would silently
          // drop real edits sitting past row 200 while keeping untouched
          // filler rows from the front of the file.
          .filter(hasUpdate);

        if (conflictCount > 0) {
          dispatch(showToast(
            `${conflictCount} row(s) had both Is Apex and Role Reporting To set — Role Reporting To was ignored for those rows since an apex role can't have a manager.`,
            'error',
          ));
        }

        const totalEdited = edited.length;
        const parsed = edited.slice(0, BATCH_LIMIT);
        const truncated = totalEdited > BATCH_LIMIT;

        if (parsed.length === 0) {
          dispatch(showToast('No rows with a Role Reporting To or Is Apex value found in the uploaded file.', 'error'));
        } else if (truncated) {
          setRows((prev) => [...prev, ...parsed]);
          dispatch(showToast(
            `Loaded ${parsed.length} of ${totalEdited} edited rows (${BATCH_LIMIT} max per upload). Split the remaining rows into another upload.`,
            'error',
          ));
        } else {
          setRows((prev) => [...prev, ...parsed]);
          dispatch(showToast(`Loaded ${parsed.length} row${parsed.length !== 1 ? 's' : ''} from Excel.`, 'success'));
        }
      } catch {
        dispatch(showToast('Failed to read the Excel file. Please ensure it is a valid .xls or .xlsx file.', 'error'));
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      dispatch(showToast('Failed to read the Excel file.', 'error'));
      setParsing(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemoveAll = () =>
    showSbConfirm({
      title: 'Remove all rows',
      content: 'This will discard all rows and cannot be undone.',
      okText: 'Remove All',
      cancelText: 'Cancel',
      onOk: () => { setRows([]); setFileList([]); },
    });

  // sb_unique_id -> roleOptions row, built once instead of a linear
  // roleOptions.find() inside enrichedRow (which was otherwise re-scanning
  // roleOptions once per column per row, per table render).
  const roleRowById = useMemo(
    () => new Map(roleOptions.map((o) => [o.value, o.row])),
    [roleOptions]
  );

  // Excel rows already carry their own display fields (parsed straight from
  // the template's locked columns); manually-added rows only have an id
  // until a role is picked, so look up jd_status/sb_requisition_number (not
  // in the template) from the fetched roleOptions by ownSbUniqueId. Computed
  // once per row (keyed by record.key) rather than independently inside
  // every column's render function.
  const enrichedByKey = useMemo(() => {
    const map = new Map();
    rows.forEach((record) => {
      const match = roleRowById.get(record.ownSbUniqueId);
      map.set(record.key, {
        jobTitle: record.jobTitle || match?.job_title || '',
        jobCode: record.jobCode || match?.job_code || '',
        sbRequisitionNumber: match?.sb_requisition_number || '',
        jdStatus: match?.jd_status || '',
        businessUnit: record.businessUnit || match?.business_unit || '',
        functionDept: record.functionDept || match?.function || '',
        level: record.level || match?.job_level || '',
        location: record.location || match?.location || '',
      });
    });
    return map;
  }, [rows, roleRowById]);
  const enrichedRow = (record) => enrichedByKey.get(record.key) || {};

  const handleSubmitAll = async () => {
    const invalid = rows.filter((r) => !r.ownSbUniqueId);
    if (invalid.length > 0) {
      dispatch(showToast('Every row must have a role selected before submitting.', 'error'));
      return;
    }

    // Matches bulk_update_role_reporting_to_service's row shape exactly — an
    // update-in-place keyed by sb_unique_id, reporting fields only. No JD
    // metadata (job_title, function, etc.) is read by that service, so
    // sending it would just be ignored. role_reporting_to_display is only
    // ever a client-side fallback label — the backend has no field for it
    // and would silently skip a row that only carries it (unresolved display
    // text is not the same as manager_sb_unique_id), so those rows are
    // excluded from the submit rather than sent as a no-op.
    const submittable = rows.filter((r) => r.isApexRole || r.roleReportingToSbUniqueId);
    const skipped = rows.length - submittable.length;

    // Nothing resolved to a real update (e.g. every row's manager only
    // matched unusable display text, or a manual row has a role picked but
    // nothing else set) — don't fire the POST or show the "queued" dialog
    // for an empty batch.
    if (submittable.length === 0) {
      dispatch(showToast('No rows have a resolved Role Reporting To or Is Apex value — nothing to submit.', 'error'));
      return;
    }

    const payload = submittable.map((r) => ({
      sb_unique_id: r.ownSbUniqueId,
      role_reporting_to_sb_unique_id: r.isApexRole ? '' : r.roleReportingToSbUniqueId || '',
      is_apex_role: !!r.isApexRole,
    }));

    const formData = new FormData();
    formData.append('metadata', JSON.stringify(payload));

    // The endpoint responds 202 once the batch is queued (Celery task,
    // progress via Redis task_stream:{task_id}) — actual row-level results
    // (updated/failed/failed_rows) aren't in this response and there's no
    // frontend-facing endpoint yet to poll task_stream, so this can only
    // confirm the batch was accepted, not that it succeeded. Awaited (not
    // fire-and-forget) so a request-level failure (network error, 4xx/5xx)
    // is caught and surfaced instead of silently discarded.
    setSubmitting(true);
    try {
      const result = await bulkUploadRoleReportingTo(formData);
      setSubmitResult({ taskId: result?.task_id, total: result?.total ?? submittable.length, skipped });
      setRows([]);
      setFileList([]);
      onSubmitSuccess?.();
    } catch (error) {
      console.error('[RoleReportingBulkUpload] Submission failed:', error);
      dispatch(showToast('Failed to submit role reporting updates. Please try again.', 'error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Read-only columns filter on the enriched values their cells show; the
  // editable columns (Job Title select, Reporting To, Is Apex) have none.
  const columns = [
    {
      title: 'Job Title',
      key: 'jobTitle',
      width: 220,
      render: (_, record) =>
        record.source === 'manual' ? (
          <SbSelect
            showSearch
            allowClear
            size="small"
            placeholder="Select role"
            options={roleOptions}
            value={record.ownSbUniqueId || undefined}
            onChange={(v) => {
              const match = roleOptions.find((o) => o.value === v)?.row;
              // Changing the row's own role doesn't revalidate an
              // already-picked manager — if the new own-role happens to be
              // the same JD that was previously set as Reporting To, the
              // row would end up reporting to itself with nothing catching
              // it client-side. Clear the manager whenever it now matches.
              const selfReferencing = v && record.roleReportingToSbUniqueId === v;
              updateRow(record.key, {
                ownSbUniqueId: v || null,
                jobTitle: match?.job_title || '',
                jobCode: match?.job_code || '',
                businessUnit: match?.business_unit || '',
                functionDept: match?.function || '',
                level: match?.job_level || '',
                location: match?.location || '',
                ...(selfReferencing ? { roleReportingToSbUniqueId: null } : {}),
              });
            }}
          />
        ) : (
          enrichedRow(record).jobTitle || '—'
        ),
    },
    { title: 'Job Code', key: 'jobCode', filterValue: (r) => enrichedRow(r).jobCode, render: (_, r) => enrichedRow(r).jobCode || '—' },
    { title: 'Requisition #', key: 'sbRequisitionNumber', filterValue: (r) => enrichedRow(r).sbRequisitionNumber, render: (_, r) => enrichedRow(r).sbRequisitionNumber || '—' },
    { title: 'Status', key: 'jdStatus', filterValue: (r) => enrichedRow(r).jdStatus, render: (_, r) => enrichedRow(r).jdStatus || '—' },
    { title: 'Business Unit', key: 'businessUnit', filterValue: (r) => enrichedRow(r).businessUnit, render: (_, r) => enrichedRow(r).businessUnit || '—' },
    { title: 'Function', key: 'functionDept', filterValue: (r) => enrichedRow(r).functionDept, render: (_, r) => enrichedRow(r).functionDept || '—' },
    { title: 'Level', key: 'level', filterValue: (r) => enrichedRow(r).level, render: (_, r) => enrichedRow(r).level || '—' },
    { title: 'Location', key: 'location', filterValue: (r) => enrichedRow(r).location, render: (_, r) => enrichedRow(r).location || '—' },
    {
      title: 'Reporting To',
      key: 'roleReportingTo',
      width: 220,
      render: (_, record) =>
        // Couldn't be resolved to an id locally (e.g. roleOptions hadn't
        // loaded yet, or the display text didn't match any known role) —
        // there is no server-side fallback for this: handleSubmitAll's
        // `submittable` filter excludes any row without a resolved
        // roleReportingToSbUniqueId, so a row stuck in this state is
        // silently dropped from the batch entirely unless the user
        // re-picks a manager from the dropdown here first.
        !record.roleReportingToSbUniqueId && record.roleReportingToDisplay ? (
          <SbTooltip content="Couldn't match this to a known role — reselect a manager or this row won't be submitted.">
            <BulkUnresolvedReportingToStyled>{record.roleReportingToDisplay} ⚠</BulkUnresolvedReportingToStyled>
          </SbTooltip>
        ) : (
          <SbSelect
            showSearch
            allowClear
            size="small"
            placeholder="Select"
            options={roleOptions.filter((o) => o.value !== record.ownSbUniqueId)}
            value={record.roleReportingToSbUniqueId || undefined}
            disabled={record.isApexRole}
            onChange={(v) => updateRow(record.key, { roleReportingToSbUniqueId: v, roleReportingToDisplay: '', isApexRole: v ? false : record.isApexRole })}
          />
        ),
    },
    {
      title: 'Is Apex',
      key: 'isApexRole',
      width: 90,
      render: (_, record) => (
        <SbSwitch
          size="small"
          checked={!!record.isApexRole}
          disabled={!!record.roleReportingToSbUniqueId || !!record.roleReportingToDisplay}
          onChange={(checked) => updateRow(record.key, {
            isApexRole: checked,
            roleReportingToSbUniqueId: checked ? null : record.roleReportingToSbUniqueId,
            roleReportingToDisplay: checked ? '' : record.roleReportingToDisplay,
          })}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <SbTooltip title="Remove row">
          <span>
            <SbButton buttonType="action" size="small" icon={<DeleteOutlined />} onClick={() => removeRow(record.key)} />
          </span>
        </SbTooltip>
      ),
    },
  ];

  return (
    <>
      <BulkToolbarStyled>
        <BulkToolbarActionsStyled>
          <SbTooltip content="Download an Excel template pre-filled with every role eligible for reporting updates.">
            <TooltipTriggerStyled>
              <SbButton buttonType="secondary" size="small" icon={<DownloadOutlined />} onClick={handleDownloadTemplate} loading={downloading}>
                Download Template
              </SbButton>
            </TooltipTriggerStyled>
          </SbTooltip>
          <SbTooltip content="Upload the completed Excel template.">
            <span>
              <SbUpload
                accept=".xls,.xlsx"
                maxCount={1}
                showUploadList={false}
                disabled={fileList.length > 0 || parsing}
                beforeUpload={async (file) => {
                  const error = await validateExcelFile(file);
                  if (error) {
                    dispatch(showToast(error, 'error'));
                    return false;
                  }
                  setFileList([file]);
                  parseExcelFile(file);
                  return false;
                }}
              >
                <SbButton buttonType="primary" size="small" icon={<UploadOutlined />} disabled={fileList.length > 0 || parsing} loading={parsing}>
                  Upload Excel
                </SbButton>
              </SbUpload>
            </span>
          </SbTooltip>
          <SbButton buttonType="secondary" size="small" icon={<PlusOutlined />} onClick={addRow}>
            Add Row
          </SbButton>
          <SbButton buttonType="secondary" size="small" onClick={handleRemoveAll} disabled={rows.length === 0}>
            Remove All
          </SbButton>
        </BulkToolbarActionsStyled>
      </BulkToolbarStyled>

      <BulkTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={rows}
          rowKey="key"
          pagination={false}
          rowClassName={(record) => (hasUpdate(record) ? '' : 'bulk-incomplete-row')}
          locale={{ emptyText: 'No rows yet — download the template, fill it in, and upload it, or click Add Row.' }}
        />
      </BulkTableWrapperStyled>

      <BulkSubmitRowStyled>
        <SbButton
          buttonType="primary"
          disabled={rows.length === 0 || submitting}
          loading={submitting}
          onClick={handleSubmitAll}
        >
          Submit All
        </SbButton>
      </BulkSubmitRowStyled>

      <SbModal
        open={!!submitResult}
        title="Batch Queued"
        closable={false}
        maskClosable={false}
        footer={
          <SbButton
            buttonType="primary"
            onClick={() => {
              setSubmitResult(null);
              onContinue?.();
            }}
          >
            Continue
          </SbButton>
        }
      >
        {submitResult?.total} row(s) queued for processing.
        {submitResult?.skipped > 0 && ` ${submitResult.skipped} row(s) had no resolved manager and were not submitted.`}
        {' '}Updates are applied in the background — check back on this page shortly to confirm each role's reporting line.
      </SbModal>
    </>
  );
};

export default RoleReportingBulkUpload;
